import { stampPanelTemplate } from "./panel-template";

const addItemsToCartWithDrawer = async (items) => {
  const requestItems = (items || [])
    .filter((item) => item && item.id)
    .map((item) => ({
      id: item.id,
      quantity: item.quantity || 1,
    }));

  if (!requestItems.length) {
    throw new Error("Add to cart failed: missing line items");
  }

  const response = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ items: requestItems }),
  });

  if (!response.ok) {
    throw new Error("Add to cart failed");
  }

  await response.json();

  const inlineCart = document.querySelector(".inline-cart");
  const inlineCartPageOverlay = document.querySelector(".inline-cart__page-overlay");

  if (inlineCart) {
    inlineCart.classList.add("active");
  }

  if (inlineCartPageOverlay) {
    inlineCartPageOverlay.classList.add("active");
  }

  document.body.classList.add("body--no-scroll");
};

const addToCart = async (button) => {
  if (!button || button.disabled) return;

  const variantId = button.getAttribute("data-variant-id");
  if (!variantId) return;

  button.disabled = true;

  try {
    const itemsToAdd = [{
      id: variantId,
      quantity: 1,
    }];

    await addItemsToCartWithDrawer(itemsToAdd);
  } catch (error) {
    console.error("Cart update failed:", error);
  } finally {
    button.disabled = false;
  }
};

const applyPanelColor = (section, tab) => {
  const panelsContainer = section.querySelector(".bestsellers-tabs__panels");
  const tabsContainer = section.querySelector(".bestsellers-tabs__tabs");
  if (!panelsContainer && !tabsContainer) return;

  const color = tab?.dataset?.panelColor;
  if (color) {
    if (tabsContainer) {
      tabsContainer.style.setProperty("--active-panel-color", color);
    }

    if (panelsContainer) {
      panelsContainer.style.setProperty("--active-panel-color", color);
    }
  } else {
    if (panelsContainer) {
      panelsContainer.style.removeProperty("--active-panel-color");
    }
    if (tabsContainer) {
      tabsContainer.style.removeProperty("--active-panel-color");
    }
  }
};

const updateScrollerNavState = (scroller, prevButton, nextButton) => {
  if (!scroller || !prevButton || !nextButton) return;

  const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  const current = Math.round(scroller.scrollLeft);
  const atStart = current <= 0;
  const atEnd = current >= Math.round(maxScrollLeft);
  const hasOverflow = maxScrollLeft > 0;

  prevButton.disabled = !hasOverflow || atStart;
  nextButton.disabled = !hasOverflow || atEnd;
};

const initScrollerControls = (section) => {
  const rails = Array.from(section.querySelectorAll(".bestsellers-tabs__rail"));

  rails.forEach((rail) => {
    const scroller = rail.querySelector("[data-bestsellers-scroller]");
    const prevButton = rail.querySelector("[data-bestsellers-prev]");
    const nextButton = rail.querySelector("[data-bestsellers-next]");

    if (!scroller || !prevButton || !nextButton) return;
    if (rail.dataset.bestsellersNavInitialized === "true") {
      updateScrollerNavState(scroller, prevButton, nextButton);
      return;
    }

    rail.dataset.bestsellersNavInitialized = "true";

    const getStep = () => Math.max(220, Math.round(scroller.clientWidth * 0.8));

    prevButton.addEventListener("click", () => {
      scroller.scrollBy({ left: -getStep(), behavior: "smooth" });
    });

    nextButton.addEventListener("click", () => {
      scroller.scrollBy({ left: getStep(), behavior: "smooth" });
    });

    scroller.addEventListener("scroll", () => {
      updateScrollerNavState(scroller, prevButton, nextButton);
    });

    updateScrollerNavState(scroller, prevButton, nextButton);
  });
};

const activateTab = (section, tab) => {
  if (!tab) return;

  const tabs = Array.from(section.querySelectorAll("[data-bestsellers-tab]"));
  const panels = Array.from(section.querySelectorAll("[data-bestsellers-panel]"));
  const targetId = tab.getAttribute("aria-controls");

  tabs.forEach((item) => {
    const isActive = item === tab;
    item.classList.toggle("is-active", isActive);
    item.setAttribute("aria-selected", isActive ? "true" : "false");
    item.setAttribute("tabindex", isActive ? "0" : "-1");
  });

  panels.forEach((panel) => {
    const isActive = panel.id === targetId;
    if (isActive) {
      // First visit to this tab: stamp its inert <template> into the DOM.
      // initScrollerControls below picks up the newly created rail.
      stampPanelTemplate(panel);
    }
    panel.hidden = !isActive;
  });

  applyPanelColor(section, tab);
  requestAnimationFrame(() => initScrollerControls(section));
};

const handleKeydown = (event, section) => {
  const tabs = Array.from(section.querySelectorAll("[data-bestsellers-tab]"));
  const currentIndex = tabs.indexOf(document.activeElement);
  if (currentIndex === -1) return;

  let nextIndex = currentIndex;
  if (event.key === "ArrowRight") {
    nextIndex = (currentIndex + 1) % tabs.length;
  } else if (event.key === "ArrowLeft") {
    nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  } else if (event.key === "Home") {
    nextIndex = 0;
  } else if (event.key === "End") {
    nextIndex = tabs.length - 1;
  } else {
    return;
  }

  event.preventDefault();
  tabs[nextIndex].focus();
  activateTab(section, tabs[nextIndex]);
};

const initSection = (section) => {
  if (!section || section.dataset.bestsellersTabsInitialized === "true") return;

  section.dataset.bestsellersTabsInitialized = "true";
  const tabs = Array.from(section.querySelectorAll("[data-bestsellers-tab]"));

  if (tabs.length) {
    tabs.forEach((tab) => {
      tab.addEventListener("click", (event) => {
        const ctaClicked = event.target.closest("[data-bestsellers-tab-cta]");
        const collectionUrl = tab.dataset.collectionUrl;
        const isActiveTab = tab.classList.contains("is-active");

        if (ctaClicked && isActiveTab && collectionUrl) {
          event.preventDefault();
          event.stopPropagation();
          window.location.href = collectionUrl;
          return;
        }

        activateTab(section, tab);
      });
    });

    section.addEventListener("keydown", (event) => handleKeydown(event, section));

    const activeTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true") || tabs[0];
    activateTab(section, activeTab);
  }

  // Delegated so buttons stamped in from a panel <template> later also work.
  section.addEventListener("click", (event) => {
    const button = event.target.closest("[data-bestsellers-add-to-cart]");
    if (button && section.contains(button)) {
      addToCart(button);
    }
  });

  initScrollerControls(section);

  window.addEventListener("resize", () => {
    initScrollerControls(section);
  });
};

const initBestsellersTabs = (root = document) => {
  if (!root) return;

  if (root.matches && root.matches("[data-bestsellers-tabs]")) {
    initSection(root);
  }

  if (root.querySelectorAll) {
    root.querySelectorAll("[data-bestsellers-tabs]").forEach(initSection);
  }
};

export { initBestsellersTabs };
